import { RouteLocationRaw, RouteLocationNormalizedLoaded, RouteLocationNamedRaw } from 'vue-router';
import { IRouting, IRoutingRegistration, IRouting as ISearchPageRouting } from '@studyportals/search-page-interface';
import { injectable } from 'inversify';
import { IRoute } from './IRoute';
import { RouteBuilder } from './RouteBuilder';
import { Ref } from 'vue';
import { IEmbeddedState } from '@/url/IEmbeddedState';
import { NavigationPerformedEvent } from '@/url/events/NavigationPerformedEvent';
import { EventAggregationService } from '@/platform/EventAggregationService';

@injectable()
export class Routing implements IRouting {
	private numberOfPerformedNavigations = 0;
	private routing: ISearchPageRouting | undefined;
	private readonly eventAggregationService: EventAggregationService;

	public readonly root: IRoute;

	public constructor(routeBuilder: RouteBuilder, eventAggregationService: EventAggregationService) {
		this.eventAggregationService = eventAggregationService;
		this.root = routeBuilder.build();
	}

	public initialize(routing: ISearchPageRouting): void {
		this.routing = routing;
	}

	public get currentRoute(): Ref<RouteLocationNormalizedLoaded> {
		return this.useRouting().currentRoute;
	}

	public async resolve(location: RouteLocationRaw): Promise<RouteLocationNormalizedLoaded & { href: string }> {
		return await this.useRouting().resolve(location) as RouteLocationNormalizedLoaded & { href: string };
	}

	public createRoutingRegistration(): IRoutingRegistration {
		return {
			routes: [this.root.toRouteDefinition()],
			beforeEach: (_to, _from, next): void => {
				next();
			},
			afterEach: (to): void => {
				const isInitialNavigation = this.numberOfPerformedNavigations++ === 0;

				this.eventAggregationService.publish(new NavigationPerformedEvent(to, isInitialNavigation));
			},
		};
	}

	public async navigateTo(location: RouteLocationRaw): Promise<void> {
		await this.useRouting().navigateTo(location);
	}

	public async toNavigationTarget(embeddedState: IEmbeddedState): Promise<RouteLocationNamedRaw | null> {
		const result = await this.root.toNavigationTarget(embeddedState);
		if (result === null) {
			return null;
		}

		return result.navigationTarget;
	}

	private useRouting(): ISearchPageRouting {
		if (this.routing === undefined) {
			throw new Error('Routing has not yet been initialized');
		}

		return this.routing;
	}
}
